import { Injectable, inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  UrlTree,
} from "@angular/router";
import { Observable, of } from "rxjs";
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { AuthenticationService } from "../shared/services/authentication.service";
import { StudentService } from "../shared/services/student.service";
import {
  Capabilities,
  CurrentUserStore,
} from "../shared/stores/current-user.store";
import { RoleTypes } from "./roletypes";

/**
 * Route guard that gates navigation on a `Capabilities` flag from
 * `CurrentUserStore`. The route declares which capability is required:
 *
 *   { path: 'experiencedays',
 *     canActivate: [AuthGuard, CapabilityGuard],
 *     data: { capability: 'canViewFollowUpDays' } }
 *
 * The same capability key can be used in templates via `*appHasCapability`,
 * giving a single source of truth for both routing and UI visibility.
 *
 * Hydration: the guard makes sure the store has the data it needs before
 * reading the capability — roles come from `authService.profile$`, and the
 * studentprofile (for profileType-dependent capabilities) is taken from the
 * cached subject or fetched on demand.
 */
@Injectable()
export class CapabilityGuard {
  private readonly auth = inject(AuthenticationService);
  private readonly studentService = inject(StudentService);
  private readonly store = inject(CurrentUserStore);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const capability = route.data?.["capability"] as
      | keyof Capabilities
      | undefined;

    if (!capability) return of(true);

    return this.auth.profile$.pipe(
      filter((profile) => !!profile),
      first(),
      switchMap((profile) => this.hydrate(profile)),
      map(() =>
        this.store.capabilities()[capability]
          ? true
          : this.router.createUrlTree(["/errors/403"])
      )
    );
  }

  canActivateChild(route: ActivatedRouteSnapshot) {
    return this.canActivate(route);
  }

  private hydrate(profile: any): Observable<void> {
    const roles: string[] = profile?.roles ?? [];
    this.store.setRoles(roles);

    const isStudent = roles.includes(RoleTypes.Student);
    if (!isStudent) {
      this.store.setProfileType(null);
      return of(void 0);
    }

    // ProfileType already known (populated by app.component or a previous nav)
    if (this.store.profileType() !== null) return of(void 0);

    // Try the cached studentprofile from the auth service first
    return this.auth.studentprofile$.pipe(
      take(1),
      switchMap((cached: any) => {
        if (cached?.profileType) {
          this.store.setProfileType(cached.profileType);
          return of(void 0);
        }

        // Fall back to a fresh fetch and seed both store and auth cache
        return this.studentService.apiGetStudentprofileCurrent().pipe(
          tap((fresh: any) => {
            if (fresh?.profileType) {
              this.store.setProfileType(fresh.profileType);
              this.auth.setStudentprofileData(fresh);
            }
          }),
          map(() => void 0),
          catchError(() => of(void 0))
        );
      })
    );
  }
}
